import { ApiError, isNetworkError } from './api.ts';
import type { Mirror } from './mirror.ts';
import { enqueue } from './outbox.ts';
import { uploadImage, type UploadedImage } from './recipes.ts';

/** Photos are sent at most this wide or high: plenty for a 1200 px variant, kind to the uplink. */
const MAX_EDGE = 2000;

/**
 * A camera photo made smaller before it is stored and sent (a 12 MP iPhone shot is 3–5 MB).
 * Falls back to the original where the browser can't decode or re-encode it.
 */
export async function shrinkPhoto(file: Blob): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 2_000_000) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    return blob ?? file;
  } catch {
    return file;
  }
}

/** Keeps a history entry's photo on the device until it can be uploaded. */
export async function keepPhoto(db: Mirror, sessionId: string, blob: Blob): Promise<void> {
  await db.photos.put({ sessionId, blob, addedAt: new Date().toISOString() });
}

export type Upload = (file: File) => Promise<UploadedImage>;

/**
 * Uploads the photos taken offline and attaches each to its entry through the outbox (so the
 * entry itself, queued before, reaches the server first). Stops at the first network failure;
 * a photo the server refuses (not an image, too large) is dropped. True when any was sent.
 */
export async function flushPhotos(db: Mirror, upload: Upload = uploadImage): Promise<boolean> {
  let sent = false;
  for (const pending of await db.photos.orderBy('addedAt').toArray()) {
    // Removed or replaced while this one was on its way: leave the newer state alone.
    const still = async () => (await db.photos.get(pending.sessionId))?.addedAt === pending.addedAt;
    try {
      const file = new File([pending.blob], 'photo.jpg', {
        type: pending.blob.type || 'image/jpeg',
      });
      const image = await upload(file);
      if (!(await still())) continue;
      // An entry deleted meanwhile just drops its photo.
      if (await db.cookSessions.get(pending.sessionId)) {
        await enqueue(db, [
          {
            entity: 'cookSession',
            op: 'upsert',
            id: pending.sessionId,
            fields: { imageId: image.id },
          },
        ]);
      }
      await db.photos.delete(pending.sessionId);
      sent = true;
    } catch (err) {
      if (isNetworkError(err) || (err instanceof ApiError && err.status === 401)) return sent;
      if (await still()) await db.photos.delete(pending.sessionId);
    }
  }
  return sent;
}
